/**
 * Cost Explorer — "where did the money go?"
 *
 * One consumption estimate, re-sliced four ways: by what it was spent on (cost
 * driver), by the template the work ran against, by the person who raised it,
 * and by how it arrived (Outlook add-in, web app, API). Every slice is priced
 * per job from the same rates /billing/summary uses, so the slices add back up
 * to the estimate above — and the header says whether they do, rather than
 * leaving the admin to trust it.
 *
 * Layout follows the question an admin is asking:
 *   headline figures → spend over time, split by the chosen dimension →
 *   who/what ranks highest, and whether each request is getting dearer →
 *   the individual requests that cost the most → when in the week work lands.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  AlertCircle, AlertTriangle, ArrowUpRight, CheckCircle2, ChevronRight, Code2, Cpu, Download,
  Layers, Mail, Monitor, Receipt, RefreshCw, Users, Waypoints,
} from 'lucide-react'
import { Card, CountUp, EmptyState, HelpTip } from './BillingPrimitives'
import CostBreakdownDrawer from './CostBreakdownDrawer'
import { downloadCsv, formatCompact, formatInt, formatUsdAs, formatUsdShort } from '../../utils/billing'
import { encodeRows } from '../../utils/billingReports'
import { buildExplorer, chartSeries, DIMENSIONS, localZoneLabel, reconciles } from '../../utils/costExplorer'
import { isAllTimeLabel } from './useBillingData'

const DIM_ICONS = { driver: Cpu, template: Layers, person: Users, source: Waypoints }
const SOURCE_ICONS = { outlook: Mail, webapp: Monitor, api: Code2, other: Cpu }

/** Fixed hues for the fixed dimensions, so "API" is the same colour everywhere. */
const FIXED_COLOR_INDEX = {
  driver: { documents: 0, normalisation: 3, email: 5, llm_fallback: 2, ai_learning: 4 },
  source: { outlook: 0, webapp: 1, api: 3 },
}

/**
 * Colour follows the entity, never its position in a filtered view: the map is
 * built from the full ranked list, so hiding a series in the legend does not
 * repaint the ones left.
 */
function colorsFor(dim, items, chart) {
  const map = new Map()
  const fixed = FIXED_COLOR_INDEX[dim]
  items.forEach((item, i) => {
    if (fixed) map.set(item.key, fixed[item.key] !== undefined ? chart.series[fixed[item.key]] : chart.neutral)
    else map.set(item.key, i < 6 ? chart.series[i] : chart.neutral)
  })
  map.set('__other', chart.neutral)
  return map
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/** Segmented control with a pill that slides to the active option. */
function DimensionSwitch({ value, onChange }) {
  const wrapRef = useRef(null)
  const [pill, setPill] = useState(null)

  useLayoutEffect(() => {
    const wrap = wrapRef.current
    const btn = wrap?.querySelector(`[data-dim="${value}"]`)
    if (!wrap || !btn) return
    const place = () => setPill({ left: btn.offsetLeft, width: btn.offsetWidth })
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [value])

  return (
    <div
      ref={wrapRef}
      role="group"
      aria-label="Break down cost by"
      className="relative inline-flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 max-w-full overflow-x-auto no-scrollbar"
    >
      {pill && (
        <span
          aria-hidden="true"
          className="absolute top-0.5 bottom-0.5 rounded-md bg-white dark:bg-gray-700 shadow-sm
                     transition-[left,width] duration-300 ease-[cubic-bezier(.16,1,.3,1)]"
          style={{ left: pill.left, width: pill.width }}
        />
      )}
      {DIMENSIONS.map(d => {
        const Icon = DIM_ICONS[d.value]
        const active = d.value === value
        return (
          <button
            key={d.value}
            type="button"
            data-dim={d.value}
            aria-pressed={active}
            onClick={() => onChange(d.value)}
            className={`relative z-10 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md
                        whitespace-nowrap transition-colors duration-200
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                        ${active
                          ? 'text-navy-700 dark:text-navy-200'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
          >
            <Icon className="w-3.5 h-3.5" />
            {d.label}
          </button>
        )
      })}
    </div>
  )
}

function Kpi({ label, value, foot, help, delay, accent = false, children }) {
  return (
    <div
      className={`group relative rounded-xl border p-4 animate-card-in transition-all duration-200
                  hover:-translate-y-0.5 hover:shadow-md
                  ${accent
                    ? 'border-navy-200 dark:border-navy-700 bg-gradient-to-b from-navy-50/80 to-white dark:from-navy-900/40 dark:to-gray-900'
                    : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-700'}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-1.5 mb-2">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
        {help && <HelpTip>{help}</HelpTip>}
      </div>
      <div className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 leading-tight truncate">
        {value}
      </div>
      {foot && <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 truncate">{foot}</p>}
      {children}
    </div>
  )
}

function SpendTooltip({ active, payload, chart, series, money }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  const lines = series
    .map(s => ({ ...s, value: row[s.key] || 0 }))
    .filter(s => s.value > 0)
    .sort((a, b) => b.value - a.value)
  const total = lines.reduce((sum, s) => sum + s.value, 0)
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg min-w-[190px] max-w-[280px]"
      style={{ background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipText }}
    >
      <p className="font-semibold mb-1.5">{row.fullLabel}</p>
      {lines.length === 0 && <p className="opacity-70">No spend</p>}
      {lines.map(s => (
        <p key={s.key} className="flex items-center gap-2 py-0.5 tabular-nums">
          <i className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: s.color }} />
          <span className="truncate opacity-80">{s.label}</span>
          <span className="ml-auto font-semibold pl-3">{money(s.value)}</span>
        </p>
      ))}
      {lines.length > 1 && (
        <p className="flex mt-1.5 pt-1.5 border-t tabular-nums" style={{ borderColor: chart.tooltipBorder }}>
          <span className="opacity-80">Total</span>
          <span className="ml-auto font-semibold">{money(total)}</span>
        </p>
      )}
    </div>
  )
}

function SpendChart({ model, dim, items, colors, chart, money, moneyShort, onSelect }) {
  const [hidden, setHidden] = useState(() => new Set())
  useEffect(() => { setHidden(new Set()) }, [dim])

  const { series, rows } = useMemo(() => chartSeries(items, model.buckets, 6), [items, model.buckets])
  const coloured = series.map(s => ({ ...s, color: colors.get(s.itemKey) || chart.neutral }))
  const visible = coloured.filter(s => !hidden.has(s.key))
  const hasData = rows.some(r => coloured.some(s => r[s.key] > 0))

  const toggle = key => setHidden(h => {
    const next = new Set(h)
    if (next.has(key)) next.delete(key)
    else if (coloured.length - next.size > 1) next.add(key)   // never hide the last one
    return next
  })

  if (!hasData) {
    return <EmptyState icon={Receipt} title="No spend in this period" body="Process a document, or pick a longer period." />
  }

  return (
    <div key={dim} className="animate-panel-in">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={rows} margin={{ top: 12, right: 8, left: 0, bottom: 4 }} barCategoryGap="22%">
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: chart.axis }}
            tickLine={false}
            axisLine={{ stroke: chart.grid }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            tick={{ fontSize: 11, fill: chart.axis }}
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={moneyShort}
          />
          <Tooltip
            cursor={{ fill: chart.grid, opacity: 0.45 }}
            content={<SpendTooltip chart={chart} series={visible} money={money} />}
          />
          {visible.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="spend"
              fill={s.color}
              stroke={chart.tooltipBg}
              strokeWidth={1}
              radius={i === visible.length - 1 ? [4, 4, 0, 0] : 0}
              maxBarSize={44}
              animationDuration={650}
              animationBegin={i * 60}
              animationEasing="ease-out"
            />
          ))}
        </BarChart>
      </ResponsiveContainer>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5 px-1 pt-3">
        {coloured.map(s => {
          const off = hidden.has(s.key)
          const item = items.find(i => i.key === s.itemKey)
          return (
            <span key={s.key} className="inline-flex items-center">
              <button
                type="button"
                onClick={() => toggle(s.key)}
                aria-pressed={!off}
                title={off ? 'Show this series' : 'Hide this series'}
                className={`inline-flex items-center gap-1.5 text-xs rounded px-1 -mx-1 transition-opacity duration-200
                            text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100
                            focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                            ${off ? 'opacity-35 line-through' : ''}`}
              >
                <i className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                <span className="max-w-[180px] truncate">{s.label}</span>
              </button>
              {item && (
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  aria-label={`Open ${s.label} details`}
                  className="ml-0.5 p-0.5 rounded text-gray-300 dark:text-gray-600 hover:text-navy-600 dark:hover:text-navy-300
                             transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
                >
                  <ArrowUpRight className="w-3 h-3" />
                </button>
              )}
            </span>
          )
        })}
      </div>
      <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2 px-1">
        Click a legend entry to hide it; the arrow opens its breakdown.
      </p>
    </div>
  )
}

function RankingList({ dim, items, colors, money, total, onSelect }) {
  const max = Math.max(0.0001, ...items.map(i => i.cost))
  const [showAll, setShowAll] = useState(false)
  useEffect(() => { setShowAll(false) }, [dim])
  const shown = showAll ? items : items.slice(0, 8)

  if (items.length === 0) {
    return <EmptyState icon={Layers} title="Nothing to rank yet" body="Nothing was processed in this period." />
  }

  return (
    <div key={dim} className="space-y-0.5">
      {shown.map((item, idx) => {
        const SourceIcon = dim === 'source' ? SOURCE_ICONS[item.key] : null
        const sub = dim === 'driver' || dim === 'source'
          ? item.plain
          : `${formatInt(item.requests)} request${item.requests === 1 ? '' : 's'}${item.perRequest !== null ? ` · ${money(item.perRequest)} each` : ''}`
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item)}
            className="group w-full grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1.3fr)_minmax(64px,1fr)_auto]
                       items-center gap-x-3 gap-y-1.5
                       px-2.5 py-2 rounded-lg text-left animate-line-in
                       hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            style={{ animationDelay: `${Math.min(idx, 10) * 35}ms` }}
          >
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[13px] font-medium text-gray-900 dark:text-gray-100">
                <i className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: colors.get(item.key) }} />
                {SourceIcon && <SourceIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />}
                <span className="truncate">{item.label}</span>
                {item.isApp && (
                  <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded
                                   bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                    Service
                  </span>
                )}
              </span>
              <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5 pl-3.5">{sub}</span>
            </span>
            {/* Below sm the bar takes its own row, so the label keeps the width. */}
            <span className="col-span-2 order-last sm:col-span-1 sm:order-none h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <span
                className="block h-full rounded-full animate-grow-x"
                style={{
                  width: `${Math.max(1.5, (item.cost / max) * 100)}%`,
                  background: colors.get(item.key),
                  animationDelay: `${Math.min(idx, 10) * 35 + 80}ms`,
                }}
              />
            </span>
            <span className="text-right flex items-center gap-1.5">
              <span>
                <span className="block text-[13px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {money(item.cost)}
                </span>
                <span className="block text-[10.5px] tabular-nums text-gray-400 dark:text-gray-500">
                  {total > 0 ? `${(item.share * 100).toFixed(1)}%` : '—'}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500
                                       dark:group-hover:text-gray-300 group-hover:translate-x-0.5 transition-all" />
            </span>
          </button>
        )
      })}
      {items.length > 8 && (
        <button
          type="button"
          onClick={() => setShowAll(v => !v)}
          className="mt-1 ml-2.5 text-xs font-medium text-navy-700 dark:text-navy-300 hover:underline
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          {showAll ? 'Show top 8' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  )
}

function UnitCostChart({ model, chart, money, moneyShort }) {
  const points = model.unitSeries.filter(p => p.perRequest !== null)
  if (points.length < 2) {
    return (
      <p className="text-[12px] text-gray-500 dark:text-gray-400 py-10 text-center">
        Needs at least two {model.grouping === 'day' ? 'days' : model.grouping === 'week' ? 'weeks' : 'months'} with
        processed requests to draw a trend.
      </p>
    )
  }
  const color = chart.series[3]
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={points} margin={{ top: 10, right: 8, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="unitCostFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: chart.axis }} tickLine={false} axisLine={{ stroke: chart.grid }} minTickGap={16} />
        <YAxis tick={{ fontSize: 11, fill: chart.axis }} tickLine={false} axisLine={false} width={52} tickFormatter={moneyShort} />
        <Tooltip
          cursor={{ stroke: chart.axis, strokeDasharray: '3 3' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null
            const p = payload[0].payload
            return (
              <div
                className="rounded-lg px-3 py-2 text-xs shadow-lg"
                style={{ background: chart.tooltipBg, border: `1px solid ${chart.tooltipBorder}`, color: chart.tooltipText }}
              >
                <p className="font-semibold mb-1">{p.fullLabel}</p>
                <p className="flex gap-3 tabular-nums"><span className="opacity-80">Per request</span><span className="ml-auto font-semibold">{money(p.perRequest)}</span></p>
                <p className="flex gap-3 tabular-nums opacity-80"><span>{formatInt(p.requests)} requests</span><span className="ml-auto">{money(p.cost)}</span></p>
              </div>
            )
          }}
        />
        <Area
          type="monotone"
          dataKey="perRequest"
          stroke={color}
          strokeWidth={2}
          fill="url(#unitCostFill)"
          dot={false}
          activeDot={{ r: 5, stroke: chart.tooltipBg, strokeWidth: 2, fill: color }}
          animationDuration={900}
          animationEasing="ease-out"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function Heatmap({ heat, chart, money }) {
  const [hover, setHover] = useState(null)
  const zone = useMemo(() => localZoneLabel(), [])
  const max = Math.max(0.0001, ...heat.flat().map(c => c.cost))
  const color = chart.series[0]
  const total = heat.flat().reduce((s, c) => s + c.jobs, 0)
  if (total === 0) return null

  return (
    <div>
      <div className="overflow-x-auto no-scrollbar">
        <div className="grid gap-[3px] min-w-[600px]" style={{ gridTemplateColumns: '34px repeat(24, minmax(0, 1fr))' }}>
          <span />
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className="text-[10px] text-center text-gray-400 dark:text-gray-500 tabular-nums">
              {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
            </span>
          ))}
          {heat.map((row, d) => (
            <div key={DAYS[d]} className="contents">
              <span className="text-[10.5px] text-gray-400 dark:text-gray-500 text-right pr-1.5 self-center">{DAYS[d]}</span>
              {row.map((cell, h) => {
                const t = cell.cost / max
                const step = cell.jobs === 0 ? 0 : Math.max(0.12, Math.ceil(t * 5) / 5)
                const label = `${DAYS[d]} ${String(h).padStart(2, '0')}:00 ${zone} — ${formatInt(cell.jobs)} job${cell.jobs === 1 ? '' : 's'}, ${money(cell.cost)}`
                return (
                  <span
                    key={h}
                    role="img"
                    aria-label={label}
                    title={label}
                    onMouseEnter={() => setHover(label)}
                    onMouseLeave={() => setHover(null)}
                    className="aspect-square rounded-[3px] bg-gray-100 dark:bg-gray-800 relative overflow-hidden
                               ring-0 hover:ring-2 hover:ring-gray-900 dark:hover:ring-gray-100 transition-shadow"
                  >
                    {/* Intensity lives on the wrapper: the entrance animation
                        ends at opacity 1, which would flatten every cell. */}
                    {step > 0 && (
                      <span className="absolute inset-0" style={{ opacity: step }}>
                        <span
                          className="absolute inset-0 animate-backdrop-in"
                          style={{ background: color, animationDelay: `${(d * 24 + h) * 3}ms` }}
                        />
                      </span>
                    )}
                  </span>
                )
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
        <span>Less</span>
        {[0.12, 0.4, 0.6, 0.8, 1].map(o => (
          <span key={o} className="w-4 h-2.5 rounded-sm" style={{ background: color, opacity: o }} />
        ))}
        <span>More spend</span>
        <span className="ml-auto tabular-nums min-h-[1em]">{hover || `Hover a cell for the figures · shown in your local time, ${zone}`}</span>
      </div>
    </div>
  )
}

function SourceBadge({ source }) {
  const Icon = SOURCE_ICONS[source] || Cpu
  const label = { outlook: 'Outlook', webapp: 'Web app', api: 'API', other: 'Other' }[source] || source
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full
                     bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 whitespace-nowrap">
      <Icon className="w-3 h-3" />
      {label}
    </span>
  )
}

function ExpensiveRequests({ rows, money, total }) {
  const navigate = useNavigate()
  const [limit, setLimit] = useState(10)
  const shown = rows.slice(0, limit)
  const max = Math.max(0.0001, ...shown.map(r => r.cost))
  if (rows.length === 0) return null

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] min-w-[720px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
              <th className="font-semibold py-2 pr-3">Request</th>
              <th className="font-semibold py-2 pr-3">Template</th>
              <th className="font-semibold py-2 pr-3">Raised by</th>
              <th className="font-semibold py-2 pr-3">Arrived via</th>
              <th className="font-semibold py-2 pr-3 text-right">Pages</th>
              <th className="font-semibold py-2 pr-3 text-right">Runs</th>
              <th className="font-semibold py-2 text-right">Cost</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, idx) => (
              <tr
                key={r.id}
                onClick={() => r.known && navigate(`/requests/${r.id}`)}
                onKeyDown={e => { if (r.known && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); navigate(`/requests/${r.id}`) } }}
                tabIndex={r.known ? 0 : -1}
                className={`border-b border-gray-100 dark:border-gray-800/80 last:border-0 animate-line-in transition-colors
                            focus:outline-none focus-visible:bg-navy-50 dark:focus-visible:bg-navy-900/30
                            ${r.known ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''}`}
                style={{ animationDelay: `${Math.min(idx, 12) * 30}ms` }}
              >
                <td className="py-2.5 pr-3 max-w-[260px]">
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate" title={r.title}>{r.title}</p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">#{r.id}{r.status ? ` · ${r.status}` : ''}</p>
                </td>
                <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300 max-w-[180px] truncate" title={r.templateName}>{r.templateName}</td>
                <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-300 max-w-[160px] truncate" title={r.personName}>{r.personName}</td>
                <td className="py-2.5 pr-3"><SourceBadge source={r.source} /></td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-300">{formatInt(r.pages)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-gray-600 dark:text-gray-300">
                  {r.runs > 1 ? <span className="font-semibold text-amber-600 dark:text-amber-400" title="Processed more than once">{r.runs}×</span> : '1'}
                </td>
                <td className="py-2.5 text-right">
                  <p className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(r.cost)}</p>
                  <div className="h-1 mt-1 ml-auto w-20 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div className="h-full rounded-full bg-navy-600 dark:bg-navy-400 animate-grow-x" style={{ width: `${(r.cost / max) * 100}%` }} />
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums mt-0.5">
                    {total > 0 ? `${((r.cost / total) * 100).toFixed(1)}% of spend` : ''}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && (
        <button
          type="button"
          onClick={() => setLimit(l => l + 15)}
          className="mt-3 text-xs font-medium text-navy-700 dark:text-navy-300 hover:underline
                     focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
        >
          Show more ({rows.length - limit} left)
        </button>
      )}
    </div>
  )
}

function LoadingState({ progress }) {
  const what = progress?.phase === 'requests' ? 'requests' : 'processing jobs'
  return (
    <div className="space-y-4" role="status">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-[104px] rounded-xl" />)}
      </div>
      <div className="skeleton h-80 rounded-xl" />
      <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Reading {what}{progress?.loaded ? ` · ${formatInt(progress.loaded)} so far` : ''}…
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The explorer
// ---------------------------------------------------------------------------

/**
 * @param periodLabel the period the explorer's own data was loaded for
 * @param summaryKey  the period the billing summary was loaded for (periodKey);
 *   the reconciliation badge only compares the two when they are the same period
 */
export default function CostExplorer({ explorer, summary, chart, displayCurrency, periodLabel, summaryKey = null, onRetry }) {
  const [dim, setDim] = useState('driver')
  const [selected, setSelected] = useState(null)

  const openItem = item => setSelected({ item, dim })
  const money = v => formatUsdAs(v, displayCurrency)
  const moneyShort = v => formatUsdShort(v, displayCurrency)
  const pricing = summary?.estimated_costs?.pricing
  const summaryTotal = summary?.estimated_costs?.period?.total_estimated_usd

  const model = useMemo(() => {
    if (!explorer.data) return null
    const { jobs, requests, users, bounds } = explorer.data
    return buildExplorer({ jobs, requests, users, pricing: pricing || undefined, bounds })
  }, [explorer.data, pricing])

  const items = useMemo(() => model?.dims[dim] || [], [model, dim])
  const colors = useMemo(() => colorsFor(dim, items, chart), [dim, items, chart])
  const driverColors = useMemo(() => colorsFor('driver', model?.dims.driver || [], chart), [model, chart])
  const dimMeta = DIMENSIONS.find(d => d.value === dim)

  // A reload that failed leaves the previous period's data in place. It must
  // not be presented as the period that was asked for, so that reads as an
  // error too rather than as old figures under a new heading.
  const stale = Boolean(explorer.data) && explorer.requestedKey != null && explorer.data.key !== explorer.requestedKey

  if (explorer.error && (!model || stale)) {
    return (
      <div className="p-5">
        <EmptyState
          icon={AlertCircle}
          title="The cost breakdown could not be loaded"
          body="The per-job figures behind this view did not come back. The estimate below is unaffected."
        />
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200
                       dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Try again
          </button>
        </div>
      </div>
    )
  }

  if (!model) return <div className="p-5"><LoadingState progress={explorer.progress} /></div>

  const { totals } = model
  // The slices can only be held against the summary's figure when both cover
  // the same period; otherwise the comparison is between two different windows.
  const sameWindow = summaryKey != null && explorer.data?.key === summaryKey
  const matches = summaryTotal !== undefined && reconciles(totals.cost, summaryTotal)
  const top = items[0]
  const periodText = isAllTimeLabel(periodLabel) ? 'all time' : `last ${periodLabel}`

  const exportCsv = () => {
    const header = [dimMeta.label, 'Cost (USD)', 'Share %', 'Requests', 'Cost per request (USD)', 'Pages', 'Tokens',
      'Document processing (USD)', 'AI inference (USD)']
    const four = v => Number(v.toFixed(4))
    const rows = items.map(i => [
      i.label, four(i.cost), Number((i.share * 100).toFixed(2)), i.requests,
      i.perRequest !== null ? four(i.perRequest) : '', i.pages, i.tokens, four(i.docCost), four(i.aiCost),
    ])
    // The reports' encoder: BOM so Excel reads accented names, and a template or
    // person named "=..." / "+..." / "@..." is defused rather than run as a formula.
    downloadCsv(`xtract-cost-by-${dim}-${new Date().toISOString().slice(0, 10)}.csv`, encodeRows([header, ...rows]))
  }

  return (
    <div className={`p-5 space-y-5 transition-opacity duration-300 ${explorer.loading ? 'opacity-60' : 'opacity-100'}`}>
      {explorer.loading && <span className="sr-only" role="status">Updating cost breakdown</span>}

      {/* ---- header ---- */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Cost explorer</h2>
            <HelpTip>
              Every processing job is priced at the rates shown in the estimate below — pages read plus AI tokens —
              then attributed to the request it ran for. Re-running a request counts again, because Azure charged
              again.{summary?.infrastructure_costs ? ' Platform infrastructure is a flat monthly cost and is not split here.' : ''}
            </HelpTip>
            {explorer.loading && <RefreshCw className="w-3.5 h-3.5 text-gray-400 animate-spin" aria-hidden="true" />}
          </div>
          <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">
            Where the Azure consumption went, {periodText} — sliced by driver, template, person and how work arrived.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          {summaryTotal !== undefined && sameWindow && !explorer.loading && (
            matches ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full
                               bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 animate-fade-in"
                    title="The slices add back up to the consumption estimate for this period">
                <CheckCircle2 className="w-3.5 h-3.5" /> Matches the billing estimate
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full
                               bg-amber-50 dark:bg-amber-900/25 text-amber-700 dark:text-amber-300 animate-fade-in"
                    title="The slices cover a different total from the estimate — usually because new jobs finished between the two reads. Refresh to line them up.">
                <AlertTriangle className="w-3.5 h-3.5" /> Covers {money(totals.cost)} of {money(summaryTotal)}
              </span>
            )
          )}
        </div>
      </div>

      {explorer.data?.truncated && (
        <p className="flex items-start gap-2 text-[12px] rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          This period has more processing history than the explorer reads in one go, so it shows the most recent
          {` ${formatInt(totals.jobs)} `}jobs. Pick a shorter period for the complete picture.
        </p>
      )}

      {/* ---- headline figures ---- */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          accent
          delay={0}
          label="Usage cost"
          value={<CountUp value={totals.cost} format={money} />}
          foot={`${money(totals.docCost)} pages · ${money(totals.aiCost)} AI`}
          help="Estimated AI processing charges for the period: document processing plus AI inference, billed by Microsoft to the Azure subscription that runs this app and separate from your plan. Excludes hosting (servers, database, storage)."
        >
          <div className="mt-2.5 flex h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 gap-px">
            {model.dims.driver.map(d => (
              <span
                key={d.key}
                className="h-full animate-grow-x"
                style={{ width: `${d.share * 100}%`, background: driverColors.get(d.key) }}
                title={`${d.label}: ${money(d.cost)}`}
              />
            ))}
          </div>
        </Kpi>
        <Kpi
          delay={60}
          label="Requests costed"
          value={<CountUp value={totals.requests} />}
          foot={`${formatInt(totals.jobs)} processing runs · ${formatCompact(totals.pages)} pages`}
          help="Requests with at least one priced processing run in the period. A request processed twice is one request and two runs."
        />
        <Kpi
          delay={120}
          label="Average per request"
          value={totals.perRequest !== null ? <CountUp value={totals.perRequest} format={money} /> : '—'}
          foot={`${formatCompact(totals.tokens)} AI tokens in total`}
          help="Usage cost divided by requests costed. Watch the trend chart below: a rising line means each request is doing more work."
        />
        <Kpi
          delay={180}
          label={`Top ${dimMeta.noun}`}
          value={<span key={`${dim}-${top?.key}`} className="animate-panel-in inline-block max-w-full truncate align-bottom text-xl">{top?.label || '—'}</span>}
          foot={top ? `${money(top.cost)} · ${(top.share * 100).toFixed(0)}% of usage cost` : 'Nothing processed'}
        />
      </div>

      {/* ---- spend over time ---- */}
      <Card className="p-5 animate-card-in" style={{ animationDelay: '200ms' }}>
        <div className="flex items-start gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Spend over time</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              By {model.grouping}, split by {dimMeta.label.toLowerCase()}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 max-w-full">
            <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
              Break down by
            </span>
            <DimensionSwitch value={dim} onChange={setDim} />
          </div>
        </div>
        <SpendChart
          model={model}
          dim={dim}
          items={items}
          colors={colors}
          chart={chart}
          money={money}
          moneyShort={moneyShort}
          onSelect={openItem}
        />
      </Card>

      {/* ---- ranking + unit cost ---- */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_.85fr] gap-5">
        <Card className="p-5 animate-card-in" style={{ animationDelay: '260ms' }}>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{dimMeta.label} ranking</h3>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
              {money(totals.cost)} total
            </span>
            <button
              type="button"
              onClick={exportCsv}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-gray-200
                         dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
          {dim === 'source' && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 flex items-start gap-1.5">
              <HelpTip label="How the channel is worked out">
                Xtract does not store a channel on each request, so it is read from what each entry point leaves
                behind. API: the request has an external reference, or came from an application identity that never
                sends email. Outlook: it came from the add-in&apos;s application identity, which sends the email with it.
                Web app: it was raised by someone signed in.
              </HelpTip>
              <span>Channel is inferred from how each request was created.</span>
            </p>
          )}
          <RankingList dim={dim} items={items} colors={colors} money={money} total={totals.cost} onSelect={openItem} />
        </Card>

        <Card className="p-5 animate-card-in" style={{ animationDelay: '320ms' }}>
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Cost per request over time</h3>
            <HelpTip>
              Usage cost in each {model.grouping} divided by the requests processed in it. A flat line means spend
              grew because volume grew; a rising one means each request is getting more expensive.
            </HelpTip>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
            {totals.perRequest !== null ? `Average ${money(totals.perRequest)} across the period` : 'No requests yet'}
          </p>
          <UnitCostChart model={model} chart={chart} money={money} moneyShort={moneyShort} />
        </Card>
      </div>

      {/* ---- most expensive requests ---- */}
      {model.requests.length > 0 && (
        <Card className="p-5 animate-card-in" style={{ animationDelay: '380ms' }}>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Most expensive requests</h3>
            <HelpTip>
              Each request&apos;s cost is every processing run it had in the period. A request run more than once is
              flagged in the Runs column — re-runs are paid for again. Click a row to open the request.
            </HelpTip>
          </div>
          <ExpensiveRequests rows={model.requests} money={money} total={totals.cost} />
        </Card>
      )}

      {/* ---- when ---- */}
      <Card className="p-5 animate-card-in" style={{ animationDelay: '440ms' }}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">When the spend happens</h3>
          <span className="text-[11px] text-gray-400 dark:text-gray-500">Processing runs by weekday and hour</span>
        </div>
        <Heatmap heat={model.heat} chart={chart} money={money} />
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
          Moving work to quieter hours does not change the cost — Azure bills per page and per token, not per hour —
          but it shows when queues build up.
        </p>
      </Card>

      <CostBreakdownDrawer
        item={selected?.item || null}
        dim={selected?.dim || dim}
        model={model}
        color={selected ? colors.get(selected.item.key) : null}
        chart={chart}
        displayCurrency={displayCurrency}
        periodLabel={periodLabel}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}
